import TextareaAutosize from 'react-textarea-autosize'
import { useMessageHistoryStore } from '../../../../stores'
import { webchatClasses } from '../../../../styles/classes'
import { Ref } from 'react'

type TextInputProps = {
  inputRef?: Ref<HTMLTextAreaElement>
  composerPlaceholder: string | undefined
  composerDisabled: boolean
  textInput: string
  historyIndex: number
  setTextInput: (value: string) => void
  setHistoryIndex: (index: number) => void
  sendComposerMessage: () => void
}

export const TextInput = ({
  inputRef,
  composerPlaceholder,
  composerDisabled,
  textInput,
  historyIndex,
  setTextInput,
  setHistoryIndex,
  sendComposerMessage,
}: TextInputProps) => {
  const messageHistory = useMessageHistoryStore((state) => state.messageHistory)

  const { input } = webchatClasses.composer

  return (
    <TextareaAutosize
      {...input}
      aria-label="Message Input"
      ref={inputRef}
      placeholder={composerPlaceholder ?? 'Type your message...'}
      disabled={composerDisabled}
      value={textInput}
      data-has-value={!!textInput}
      onChange={(e) => {
        const newValue = e.target.value
        setTextInput(newValue)
        if (!messageHistory.includes(newValue)) {
          setHistoryIndex(-1)
        }
      }}
      maxRows={5}
      onKeyDown={(e) => {
        if (composerDisabled) return

        const native = e.nativeEvent as KeyboardEvent
        if (native.isComposing || native.keyCode === 229) {
          //KeyCode 229 is for safari browsers as the isComposing property is not always supported
          return
        }

        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault()
          setTextInput(`${textInput}\n`)
        } else if (e.key === 'Enter') {
          e.preventDefault()
          sendComposerMessage()
        }
        if (e.key === 'ArrowUp') {
          // Only navigate history if the field is empty or (the input is already in the history AND we're actively browsing)
          if (textInput.trim() === '' || (messageHistory.includes(textInput) && historyIndex >= 0)) {
            e.preventDefault()

            if (historyIndex < messageHistory.length - 1) {
              setTextInput(messageHistory[historyIndex + 1] ?? '')
            }
            setHistoryIndex(Math.min(historyIndex + 1, messageHistory.length - 1))
          }
        }
        if (e.key === 'ArrowDown') {
          // Only navigate history if the field is empty or the input is already in the history
          // Don't navigate if we're not actively navigating history (historyIndex === -1)
          if ((textInput.trim() === '' || messageHistory.includes(textInput)) && historyIndex >= 0) {
            e.preventDefault()
            if (historyIndex === 0) {
              setTextInput('')
            } else {
              setTextInput(messageHistory[historyIndex - 1] ?? '')
            }
            setHistoryIndex(Math.max(historyIndex - 1, -1))
          }
        }
      }}
    />
  )
}
